import { useMemo } from 'react'
import { Button, Input } from '@fullscreendigital/carrefour-dex-fe'
import { Modal } from 'components/core-ui/Modal'
import { useFormik } from 'formik'
import { getInitialValues, getValidationSchema } from './helpers'
import { useNotification } from 'hooks'
import { formatErrorMessage } from 'utils/formatting'
import { makeCorePostRequest, makeCorePutRequest } from 'utils/api'
import { useMutation } from 'react-query'
import { Grid } from '@mui/material'
import s from 'components/core-ui/Modal/Modal.modules.scss'
import {
  getMetiFamiliesByRayonOptions,
  getMetiSubFamiliesByFamilyOptions,
  getMetiUBBySubFamilyOptions
} from 'components/shop-reception/admin/api'
import { AsyncAutocomplete } from 'components/core-ui/AsyncAutocomplete'

interface SGRProductsProps {
  isOpen: boolean
  closeModal: Function
  setShouldRefreshData: Function
  isEdit?: boolean
  editItem?: SGRProducts
  shopTypes: AutocompleteOption[]
  shopCodes: AutocompleteOption[]
  metiRayons: AutocompleteOption[]
}

const SGRProductsForm: React.FC<SGRProductsProps> = props => {
  const {
    isOpen,
    closeModal,
    isEdit,
    editItem,
    setShouldRefreshData,
    shopTypes,
    shopCodes,
    metiRayons
  } = props
  const { showNotification } = useNotification()

  const mutateSgrProduct = async values => {
    editItem
      ? await makeCorePutRequest(`/shop-reception/products/sgr/${editItem.id}`, values)
      : await makeCorePostRequest('/shop-reception/products/sgr', values)
  }

  const { isLoading, mutate } = useMutation(mutateSgrProduct, {
    onSuccess: () => {
      closeModal()
      showNotification({
        message: `Produsul a fost ${editItem ? 'modificat' : 'creat'} cu succes.`,
        type: 'success'
      })
      setShouldRefreshData(true)
      formik.resetForm()
      closeModal()
    },
    onError: res => {
      showNotification({
        message: `A aparut o eroare la ${
          editItem ? 'modificarea' : 'crearea'
        } produsului. ${formatErrorMessage(res)}`,
        type: 'error'
      })
    }
  })

  const initialValues = useMemo(() => getInitialValues(editItem), [editItem])
  const validationSchema = useMemo(() => getValidationSchema(), [editItem])

  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: false,
    initialValues: initialValues,
    validationSchema: validationSchema,
    onSubmit: async (values: any) => {
      const payload = {
        ...values,
        famCode: values.famCode?.value || values.famCode || null,
        rayCode: values.rayCode?.value || values.rayCode || null,
        sfaCode: values.sfaCode?.value || values.sfaCode || null,
        shopCode: values.shopCode?.value || values.shopCode || null,
        shopType: values.shopType?.value || null,
        ubCode: values.ubCode?.value || values.ubCode || null
      }

      mutate(payload)
    }
  })

  const { data: metiFamiliesOptions } = getMetiFamiliesByRayonOptions(
    showNotification,
    formik.values.rayCode?.value
  )

  const { data: metiSubfamiliesOption } = getMetiSubFamiliesByFamilyOptions(
    showNotification,
    formik.values.famCode?.value
  )

  const { data: ubCodeOptions } = getMetiUBBySubFamilyOptions(
    showNotification,
    formik.values.sfaCode?.value
  )

  return (
    <Modal
      isModalOpen={isOpen}
      closeModal={closeModal}
      modalTitle={`${isEdit ? 'Editeaza' : 'Adauga'} regula produse SGR`}
      modalWidth="50%"
    >
      <Grid container spacing={2}>
        <Grid xs={12} item>
          <AsyncAutocomplete
            variant="outlined"
            label="Magazin"
            name="shopCode"
            value={formik.values.shopCode}
            options={shopCodes || []}
            onChange={option => {
              formik.setFieldValue('shopCode', option)
              formik.setFieldValue('shopType', null)
              formik.setFieldValue('rayCode', null)
              formik.setFieldValue('famCode', null)
              formik.setFieldValue('sfaCode', null)
              formik.setFieldValue('ubCode', null)
            }}
            disabled={formik.values.shopCode === null && formik.values.shopType !== null}
          />
        </Grid>
        <Grid xs={12} item>
          <AsyncAutocomplete
            variant="outlined"
            label="Regula magazin"
            name="shopType"
            value={formik.values.shopType}
            options={shopTypes || []}
            onChange={option => {
              formik.setFieldValue('shopType', option)
              formik.setFieldValue('shopCode', null)
              formik.setFieldValue('rayCode', null)
              formik.setFieldValue('famCode', null)
              formik.setFieldValue('sfaCode', null)
              formik.setFieldValue('ubCode', null)
            }}
            disabled={formik.values.shopType === null && formik.values.shopCode !== null}
          />
        </Grid>

        <Grid xs={12} item>
          <AsyncAutocomplete
            variant="outlined"
            label="Raion"
            name="rayCode"
            value={formik.values.rayCode}
            options={metiRayons || []}
            required
            onChange={option => {
              formik.setFieldValue('rayCode', option)
              formik.setFieldValue('famCode', null)
              formik.setFieldValue('sfaCode', null)
              formik.setFieldValue('ubCode', null)
            }}
            error={!!formik.errors.rayCode}
            helperText={formik.errors.rayCode as string}
          />
        </Grid>

        <Grid xs={12} item>
          <AsyncAutocomplete
            variant="outlined"
            label="Familie"
            name="famCode"
            value={formik.values.famCode}
            options={metiFamiliesOptions || []}
            onChange={option => {
              formik.setFieldValue('famCode', option)
              formik.setFieldValue('sfaCode', null)
              formik.setFieldValue('ubCode', null)
            }}
          />
        </Grid>

        <Grid xs={12} item>
          <AsyncAutocomplete
            variant="outlined"
            label="Subfamilie"
            name="sfaCode"
            value={formik.values.sfaCode}
            options={metiSubfamiliesOption || []}
            onChange={option => {
              formik.setFieldValue('sfaCode', option)
              formik.setFieldValue('ubCode', null)
            }}
          />
        </Grid>

        <Grid xs={12} item>
          <AsyncAutocomplete
            variant="outlined"
            label="Cod UB"
            name="ubCode"
            value={formik.values.ubCode}
            options={ubCodeOptions || []}
            onChange={option => formik.setFieldValue('ubCode', option)}
          />
        </Grid>

        <Grid xs={12} item>
          <Input
            type="text"
            value={formik.values.prodCode}
            onChange={event =>
              formik.setFieldValue('prodCode', (event.target as HTMLInputElement).value)
            }
            label="Cod produs"
            variant="outlined"
            style={{ width: '100%' }}
          />
        </Grid>

        <Grid xs={12} item className={s.modalButtons}>
          <Button variant="outlined" label="Anuleaza" onClick={() => closeModal()} />
          <Button
            variant="contained"
            label={'Salveaza'}
            onClick={() => formik.handleSubmit()}
            disabled={isLoading}
          />
        </Grid>
      </Grid>
    </Modal>
  )
}

export default SGRProductsForm
